import { ZodSchema, ZodTypeDef } from 'zod'
import { AxiosResponse } from 'axios'
import { fromZodError } from 'zod-validation-error'
import { format } from 'date-fns'

const timeout = (delay: number) => {
  return new Promise((resolve) => setTimeout(resolve, delay))
}

export function classNames(
  ...classes: (string | false | null | undefined)[]
): string {
  return classes.filter(Boolean).join(' ')
}

export function validateResponse<Output>(
  schema: ZodSchema<Output, ZodTypeDef, unknown>,
) {
  return function (response: AxiosResponse<Output>): Output {
    const result = schema.safeParse(response.data)
    if (result.success) {
      return result.data
    }
    console.warn(
      fromZodError(result.error, { prefix: `(URL: ${response.config.url})` }),
    )
    return response.data
  }
}

/**
 * Handles the case where the API sends dates in `yyyy-MM-dd` format
 *
 * We need to append a time string to the date, otherwise the Date object
 * will be considered UTC and when formatting back to ISO we might end up
 * on the previous or following day.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date#sect2
 */
export function dateFromIsoString(isoString: string): Date {
  return new Date(isoString.split('T')[0] + 'T00:00:00')
}

/**
 * Converts a Date or timestamp in browser's timezone into a UTC-0 timestamp
 */
const toUtc0Timestamp = (dateOrTimestamp: Date | number): number => {
  const utc0IsoString = `${format(dateOrTimestamp, 'yyyy-MM-dd')}T00:00:00.000Z`
  return new Date(utc0IsoString).getTime()
}

/**
 * Converts a UTC-0 timestamp (common for API data) into browser's timezone
 */
const toLocalTimestamp = (utcTimestamp: number): number => {
  const utcDate = new Date(utcTimestamp)

  // Crear nueva fecha con los mismos año/mes/día pero en timezone local
  return new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
  ).getTime()
}

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format

function getTimestamp(date?: Date, time?: string): number {
  if (!date || !time) return 0
  const [hours, minutes] = time.split(':').map(Number)
  const dateMilliseconds = date.getTime()
  const hoursMilliseconds = (hours || 0) * 60 * 60 * 1000
  const minutesMilliseconds = (minutes || 0) * 60 * 1000
  return dateMilliseconds + hoursMilliseconds + minutesMilliseconds
}

const getAge = (dateString: number): number => {
  const today = new Date()
  const birthDate = new Date(dateString)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

export {
  toLocalTimestamp,
  toUtc0Timestamp,
  currencyFormat,
  getTimestamp,
  timeout,
  getAge,
}
